import { useCallback, useMemo, useState } from "react";
import { Loading } from "@/components/atoms";
import { DataTable, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator, usePagination, useSorting } from "@/hooks";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { vehicleApi } from "@/redux/apis/vehicle/vehicleApi";
import { useUpdateAffiliateVehicleMutation } from "@/redux/apis/vehicleGuest/vehicleGuestApi";
import { useAppDispatch } from "@/redux/hooks";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { Page } from "@/redux/types";
import { addToast } from "@/utils";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";
import { affiliateVehiclesColumns } from "./fixtures";
import { generateAffiliateVehicleTableData } from "./helpers";
import { SelectVehicleTypeModal } from "./modals/SelectVehicleTypeModal";

export interface AffiliateVehiclesTableProps {
  items: Vehicle[];
  setPendingArchive: (vehicle: Vehicle) => void;
}

export const AffiliateVehiclesTable = ({ items, setPendingArchive }: AffiliateVehiclesTableProps) => {
  const dispatch = useAppDispatch();
  const { id: operatorId } = useOperator();
  const { sorting, setSorting } = useSorting();
  const [updateAffiliateVehicle, { isLoading }] = useUpdateAffiliateVehicleMutation();
  const [vehicleTypeModal, setVehicleTypeModal] = useState<{ vehicle: Vehicle; approveOnSave: boolean } | undefined>();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();

  const onArchiveClick = useCallback(
    (vehicle: Vehicle) => {
      setPendingArchive(vehicle);
    },
    [setPendingArchive]
  );

  const handleSelectVehicleType = (vehicle: Vehicle) => {
    if (needsTypeMigration) {
      setShowSetupModal(true);
      return;
    }
    setVehicleTypeModal({ vehicle, approveOnSave: true });
  };

  const handleAssignVehicleType = (vehicle: Vehicle) => {
    if (needsTypeMigration) {
      setShowSetupModal(true);
      return;
    }
    setVehicleTypeModal({ vehicle, approveOnSave: false });
  };

  const handleApprove = (vehicle: Vehicle, isReapprove?: boolean) => {
    if (needsTypeMigration) {
      setShowSetupModal(true);
      return;
    }
    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      approval_status: "APPROVED",
    })
      .unwrap()
      .then(() => {
        if (isReapprove) addToast("success", "Vehicle reapproved successfully");
        else addToast("success", "Vehicle approved successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleDeny = (vehicle: Vehicle) => {
    updateAffiliateVehicle({
      ownerId: operatorId,
      vehicleId: vehicle.uuid,
      approval_status: "DENIED",
      approval_reason: "Vehicle denied by operator",
    })
      .unwrap()
      .then(() => {
        addToast("success", "Vehicle denied successfully");
        dispatch(vehicleApi.util.invalidateTags(["Vehicle"]));
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const data = useMemo(
    () => generateAffiliateVehicleTableData(operatorId, items, handleApprove, handleDeny, onArchiveClick, handleSelectVehicleType, handleAssignVehicleType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, operatorId]
  );

  return (
    <>
      <div className="relative">
        {isLoading && <Loading className="rounded-lg" />}
        <DataTable
          className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
          data={data}
          columns={affiliateVehiclesColumns}
          sorting={sorting}
          setSorting={setSorting}
        />
      </div>
      <SelectVehicleTypeModal
        vehicle={vehicleTypeModal?.vehicle}
        onClose={() => setVehicleTypeModal(undefined)}
        approveOnSave={vehicleTypeModal?.approveOnSave}
      />
      <VehicleTypesSetupModal open={showSetupModal} onClose={() => setShowSetupModal(false)} valid={typesValid} total={typesTotal} />
    </>
  );
};

const AffiliateVehiclesTablePagination = ({ page }: { page: Page }) => {
  const { current, size, total, count } = page;
  const { handlePageClick, handleSizeChange } = usePagination("affiliate-vehicles", 30);

  return (
    <Pagination
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      totalItems={total}
      pageCount={count}
    />
  );
};

AffiliateVehiclesTable.Pagination = AffiliateVehiclesTablePagination;
